import { CalendarRange, Download, Gauge, Search } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { exportarCsv } from '@/lib/export';
import { cn, formatCurrency, formatDateTimeValue, formatHoraValue, formatNumber } from '@/lib/utils';

import { usePts2Auxiliares, usePts2Reporte } from '../monitoreo.api';
import { aLocal } from '../lib/pts2-format';
import type { Pts2Comprobantes, Pts2Filtros } from '../monitoreo.types';

/** Columnas de las tres tablas del reporte. */
const COLUMNAS_PRODUCTO = 'grid grid-cols-[minmax(0,1fr)_7rem_7rem_7rem_6rem] items-center gap-x-3';
const COLUMNAS_TRANSACCION =
  'grid grid-cols-[6.5rem_9rem_3.5rem_minmax(0,1fr)_6.5rem_6.5rem_6.5rem_7rem_7rem_5.5rem] items-center gap-x-3';
const COLUMNAS_MANGUERA =
  'grid grid-cols-[2.5rem_5.5rem_5.5rem_minmax(0,1fr)_7rem_7rem_7rem_7rem_7rem_7rem_6rem] items-center gap-x-3';

/** Filtros por defecto: desde el inicio del día hasta ahora (como el panel). */
function filtrosIniciales(): Pts2Filtros {
  const ahora = new Date();
  const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 0, 0);

  return {
    lados: [],
    productos: [],
    fecha_inicio: aLocal(inicio),
    fecha_fin: aLocal(ahora),
    comprobantes: 'todos',
  };
}

const COMPROBANTES: { id: Pts2Comprobantes; label: string }[] = [
  { id: 'todos', label: 'Todos' },
  { id: 'generados', label: 'Con comprobante' },
  { id: 'pendientes', label: 'Pendientes' },
];

/**
 * Reporte Controlador Ventas (`/monitoreo/pts2`).
 *
 * Puerto de `Reportes::PTS2`: las tres tablas del panel anterior (resumen por
 * producto, transacciones del controlador y resumen de mangueras con los
 * contómetros al iniciar y finalizar).
 */
export function Pts2Page() {
  useDocumentTitle('Monitoreo · Reporte Controlador Ventas');

  const [filtros, setFiltros] = useState<Pts2Filtros>(() => filtrosIniciales());

  const auxiliares = usePts2Auxiliares();
  const reporte = usePts2Reporte(filtros);

  const datos = reporte.data;
  // El detalle transacción por transacción se pagina en el navegador (antes se cortaba en
  // las 300 primeras, así que el resto no se podía ver en pantalla).
  const paginacion = usePaginacion(datos?.transactions ?? [], { clave: filtros });
  const lados = auxiliares.data?.pumps ?? [];
  const productos = auxiliares.data?.products ?? [];

  /** Alterna un valor de la lista de filtros. */
  const alternar = (campo: 'lados' | 'productos', valor: number) => {
    setFiltros((prev) => {
      const actuales = prev[campo];
      const siguiente = actuales.includes(valor)
        ? actuales.filter((item) => item !== valor)
        : [...actuales, valor];

      return { ...prev, [campo]: siguiente };
    });
  };

  const exportar = () => {
    if (!datos) return;

    exportarCsv(
      'reporte-controlador',
      [
        'Transacción',
        'Fecha',
        'Lado',
        'Manguera',
        'Producto',
        'Precio',
        'Volumen',
        'Monto',
        'Contómetro volumen',
        'Contómetro monto',
        'Comprobante',
        'Calibración',
      ],
      datos.transactions.map((fila) => [
        fila.Transaction,
        fila.DateTime,
        fila.Pump,
        fila.Nozzle,
        fila.Product,
        fila.Price.toFixed(2),
        fila.Volume.toFixed(3),
        fila.Amount.toFixed(2),
        fila.TotalVolume.toFixed(3),
        fila.TotalAmount.toFixed(2),
        fila.Serie ?? (fila.Invoiced === 1 ? 'Generado' : 'Pendiente'),
        fila.Calibration ?? '',
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Gauge className="size-6 text-brand-500" aria-hidden="true" />
            Reporte Controlador Ventas
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Transacciones registradas por el controlador, con los contómetros de cada manguera.
          </p>
        </div>

        <Button
          size="sm"
          variant="outline"
          leftIcon={<Download className="size-3.5" aria-hidden="true" />}
          onClick={exportar}
          disabled={!datos || datos.transactions.length === 0}
        >
          Exportar
        </Button>
      </header>

      {reporte.isError ? (
        <Alert variant="error" title="No se pudo generar el reporte">
          <div className="flex flex-wrap items-center gap-3">
            <span>Revisa el rango de fechas e inténtalo nuevamente.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void reporte.refetch()}
              loading={reporte.isRefetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      <SectionCard
        title="Filtros"
        description="Sin selección se incluyen todos los lados y todos los combustibles"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
              Desde
              <CampoFecha
               
                value={filtros.fecha_inicio}
                onChange={(event) => setFiltros({ ...filtros, fecha_inicio: event.target.value })}
                className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
               tipo="datetime-local" />
            </label>

            <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
              Hasta
              <CampoFecha
               
                value={filtros.fecha_fin}
                onChange={(event) => setFiltros({ ...filtros, fecha_fin: event.target.value })}
                className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
               tipo="datetime-local" />
            </label>

            <div className="flex flex-wrap items-center gap-2 pb-0.5">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Search className="size-3.5" aria-hidden="true" />}
                onClick={() => void reporte.refetch()}
                loading={reporte.isRefetching}
              >
                Generar
              </Button>

              <button
                type="button"
                onClick={() => setFiltros({ ...filtros, ...filtrosIniciales() })}
                className="inline-flex items-center gap-1.5 rounded-control border border-ink-200 bg-white px-2.5 py-2 text-xs font-medium text-ink-600 transition-colors hover:border-brand-300 hover:text-brand-700"
              >
                <CalendarRange className="size-3.5" aria-hidden="true" />
                Reiniciar rango
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Lados</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {lados.length === 0 ? (
                  <span className="text-xs text-ink-400">Sin lados configurados</span>
                ) : (
                  lados.map((lado) => {
                    const activo = filtros.lados.includes(lado.controllerPump);

                    return (
                      <button
                        key={lado.id}
                        type="button"
                        aria-pressed={activo}
                        onClick={() => alternar('lados', lado.controllerPump)}
                        title={lado.nombre_maquina_lado ?? undefined}
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                          activo
                            ? 'border-brand-500 bg-brand-600 text-white'
                            : 'border-ink-200 bg-white text-ink-600 hover:border-brand-300',
                        )}
                      >
                        Lado {lado.controllerPump}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Combustibles
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {productos.map((producto) => {
                  const activo = filtros.productos.includes(producto.id);

                  return (
                    <button
                      key={producto.id}
                      type="button"
                      aria-pressed={activo}
                      onClick={() => alternar('productos', producto.id)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                        activo
                          ? 'border-brand-500 bg-brand-600 text-white'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-300',
                      )}
                    >
                      {producto.nombre}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Comprobantes
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {COMPROBANTES.map((opcion) => (
                  <button
                    key={opcion.id}
                    type="button"
                    aria-pressed={filtros.comprobantes === opcion.id}
                    onClick={() => setFiltros({ ...filtros, comprobantes: opcion.id })}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                      filtros.comprobantes === opcion.id
                        ? 'border-brand-500 bg-brand-600 text-white'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-300',
                    )}
                  >
                    {opcion.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {reporte.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-card" />
          <Skeleton className="h-72 rounded-card" />
        </div>
      ) : !datos ? null : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Transacciones
              </p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
                {formatNumber(datos.totals.total_transactions)}
              </p>
              <p className="mt-0.5 text-xs text-ink-500">
                {formatDateTimeValue(datos.params.fecha_inicio)} →{' '}
                {formatHoraValue(datos.params.fecha_fin)}
              </p>
            </div>

            <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Volumen registrado
              </p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
                {formatNumber(datos.totals.total_volume)} gal
              </p>
              <p className="mt-0.5 text-xs text-ink-500">
                Contómetros: {formatNumber(datos.totals.total_volume_diff)} gal
              </p>
            </div>

            <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Monto registrado
              </p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
                {formatCurrency(datos.totals.total_amount)}
              </p>
              <p className="mt-0.5 text-xs text-ink-500">
                Contómetros: {formatCurrency(datos.totals.total_amount_diff)}
              </p>
            </div>

            <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Mangueras con movimiento
              </p>
              <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
                {formatNumber(datos.pump_summary.length)}
              </p>
              <p className="mt-0.5 text-xs text-ink-500">
                {datos.summary_by_product.length} productos
              </p>
            </div>
          </div>

          <SectionCard
            title="Resumen por producto"
            description="Volumen y monto llenados en el período"
            action={<Badge variant="outline">{datos.summary_by_product.length}</Badge>}
          >
            {datos.summary_by_product.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-500">
                Sin transacciones en el período seleccionado.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[34rem]">
                  <div
                    className={cn(
                      COLUMNAS_PRODUCTO,
                      'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
                    )}
                  >
                    <span>Combustible</span>
                    <span className="text-right">Precio</span>
                    <span className="text-right">Volumen</span>
                    <span className="text-right">Monto</span>
                    <span className="text-right">Ops</span>
                  </div>

                  <ul className="mt-1">
                    {datos.summary_by_product.map((fila) => (
                      <li
                        key={fila.product}
                        className={cn(COLUMNAS_PRODUCTO, 'border-b border-ink-100 py-2 last:border-b-0')}
                      >
                        <span className="truncate text-sm font-medium text-ink-800">
                          {fila.product}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {formatCurrency(fila.price)}
                        </span>
                        <span className="text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
                          {formatNumber(fila.volume)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-800 tabular-nums">
                          {formatCurrency(fila.amount)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                          {formatNumber(fila.transactions)}
                        </span>
                      </li>
                    ))}

                    <li
                      className={cn(
                        COLUMNAS_PRODUCTO,
                        'border-t-2 border-ink-200 py-2 font-semibold',
                      )}
                    >
                      <span className="text-sm text-ink-900">Total</span>
                      <span />
                      <span className="text-right font-mono text-xs text-ink-900 tabular-nums">
                        {formatNumber(datos.totals.total_volume)}
                      </span>
                      <span className="text-right font-mono text-xs text-ink-900 tabular-nums">
                        {formatCurrency(datos.totals.total_amount)}
                      </span>
                      <span className="text-right font-mono text-xs text-ink-900 tabular-nums">
                        {formatNumber(datos.totals.total_transactions)}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Resumen de mangueras"
            description="Contómetros del controlador al iniciar y al finalizar el período"
            action={<Badge variant="outline">{datos.pump_summary.length}</Badge>}
          >
            {datos.pump_summary.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-500">Sin mangueras con movimiento.</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[80rem]">
                  <div
                    className={cn(
                      COLUMNAS_MANGUERA,
                      'border-b border-ink-100 pb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-400',
                    )}
                  >
                    <span>#</span>
                    <span>Lado</span>
                    <span>Manguera</span>
                    <span>Combustible</span>
                    <span className="text-right">Vol. inicio</span>
                    <span className="text-right">Vol. fin</span>
                    <span className="text-right">Dif. vol.</span>
                    <span className="text-right">Vol. registrado</span>
                    <span className="text-right">Monto inicio</span>
                    <span className="text-right">Monto fin</span>
                    <span className="text-right">Dif. monto</span>
                  </div>

                  <ul className="mt-1">
                    {datos.pump_summary.map((fila, indice) => (
                      <li
                        key={`${fila.pump}-${fila.nozzle}`}
                        className={cn(
                          COLUMNAS_MANGUERA,
                          'border-b border-ink-100 py-2 last:border-b-0',
                        )}
                      >
                        <span className="font-mono text-xs text-ink-400 tabular-nums">
                          {indice + 1}
                        </span>
                        <span className="text-xs font-medium text-ink-800">{fila.pump}</span>
                        <span className="text-xs text-ink-600">{fila.nozzle}</span>
                        <span className="truncate text-xs text-ink-700">{fila.product}</span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {formatNumber(fila.total_volume_start)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {formatNumber(fila.total_volume_end)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-700 tabular-nums">
                          {formatNumber(fila.total_volume_diff)}
                        </span>
                        <span className="text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
                          {formatNumber(fila.volume)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {formatCurrency(fila.total_amount_start)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {formatCurrency(fila.total_amount_end)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-700 tabular-nums">
                          {formatCurrency(fila.total_amount_diff)}
                        </span>
                      </li>
                    ))}

                    <li
                      className={cn(COLUMNAS_MANGUERA, 'border-t-2 border-ink-200 py-2 font-semibold')}
                    >
                      <span />
                      <span />
                      <span />
                      <span className="text-xs text-ink-900">Total</span>
                      <span />
                      <span />
                      <span className="text-right font-mono text-xs text-ink-900 tabular-nums">
                        {formatNumber(datos.totals.total_volume_diff)}
                      </span>
                      <span className="text-right font-mono text-xs text-ink-900 tabular-nums">
                        {formatNumber(datos.totals.total_volume_metered)}
                      </span>
                      <span />
                      <span />
                      <span className="text-right font-mono text-xs text-ink-900 tabular-nums">
                        {formatCurrency(datos.totals.total_amount_diff)}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Reporte del controlador"
            description="Detalle transacción por transacción"
            action={<Badge variant="outline">{datos.transactions.length}</Badge>}
          >
            {datos.transactions.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-500">
                Sin transacciones en el período seleccionado.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[76rem]">
                  <div
                    className={cn(
                      COLUMNAS_TRANSACCION,
                      'border-b border-ink-100 pb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-400',
                    )}
                  >
                    <span>Transacción</span>
                    <span>Fecha</span>
                    <span className="text-right">Lado</span>
                    <span>Combustible</span>
                    <span className="text-right">Manguera</span>
                    <span className="text-right">Precio</span>
                    <span className="text-right">Volumen</span>
                    <span className="text-right">Monto</span>
                    <span className="text-right">Contómetro</span>
                    <span>Comprobante</span>
                  </div>

                  <ul className="mt-1">
                    {paginacion.visibles.map((fila) => (
                      <li
                        key={fila.Transaction}
                        className={cn(
                          COLUMNAS_TRANSACCION,
                          'border-b border-ink-100 py-1.5 last:border-b-0',
                        )}
                      >
                        <span className="font-mono text-xs font-semibold text-ink-700 tabular-nums">
                          {fila.Transaction}
                        </span>
                        <span className="text-xs text-ink-600">
                          {formatDateTimeValue(fila.DateTime)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {fila.Pump}
                        </span>
                        <span className="truncate text-xs text-ink-700">{fila.Product}</span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {fila.Nozzle}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                          {formatCurrency(fila.Price)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-800 tabular-nums">
                          {formatNumber(fila.Volume)}
                        </span>
                        <span className="text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
                          {formatCurrency(fila.Amount)}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                          {formatNumber(fila.TotalVolume)}
                        </span>
                        <span className="truncate text-xs">
                          {fila.Serie ? (
                            <span className="text-ink-700">{fila.Serie}</span>
                          ) : (
                            <span className="text-ink-400">
                              {fila.Calibration ? `Calibración #${fila.Calibration}` : 'Pendiente'}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {datos.transactions.length > 0 ? (
              <Paginador {...paginacion} etiqueta="transacciones" />
            ) : null}
          </SectionCard>
        </>
      )}
    </div>
  );
}
